import React, { createContext, ReactNode, useContext, useRef } from "react";

// Define the context interface
interface ReplayControlContextType {
  registerReplayControl: (deviceId: string, controls: ReplayControls) => void;
  unregisterReplayControl: (deviceId: string) => void;
  startReplay: (deviceId?: string) => void;
  pauseReplay: (deviceId?: string) => void;
  resetReplay: (deviceId?: string) => void;
  startAllReplays: () => void;
  pauseAllReplays: () => void;
  resetAllReplays: () => void;
  navigateToNextPoint: (deviceId: string) => void;
  navigateToPrevPoint: (deviceId: string) => void;
  getCurrentPointInfo: (deviceId: string) => { currentIndex: number; totalPoints: number; hasNext: boolean; hasPrev: boolean } | null;
}

interface ReplayControls {
  startPlay: () => void;
  pausePlay: () => void;
  resetPath: () => void;
  navigateToNextPoint?: () => void;
  navigateToPrevPoint?: () => void;
  getCurrentPointInfo?: () => { currentIndex: number; totalPoints: number; hasNext: boolean; hasPrev: boolean };
}

// Create the context
const ReplayControlContext = createContext<ReplayControlContextType | undefined>(undefined);

// Provider component
export const ReplayControlProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const controlsRef = useRef<Map<string, ReplayControls>>(new Map());

  const registerReplayControl = (deviceId: string, controls: ReplayControls) => {
    controlsRef.current.set(deviceId, controls);
    console.log('[ReplayContext] Registered deviceIds:', Array.from(controlsRef.current.keys()));
  };

  const unregisterReplayControl = (deviceId: string) => {
    controlsRef.current.delete(deviceId);
    console.log('[ReplayContext] Registered deviceIds:', Array.from(controlsRef.current.keys()));
  };

  const startReplay = (deviceId?: string) => {
    console.log('[ReplayContext] startReplay called with deviceId:', deviceId);
    console.log('[ReplayContext] Available controls:', Array.from(controlsRef.current.keys()));

    if (deviceId) {
      const controls = controlsRef.current.get(deviceId);
      if (controls) {
        console.log('[ReplayContext] Found controls for deviceId:', deviceId, 'Starting replay...');
        controls.startPlay();
      } else {
        console.warn(`[ReplayContext] No controls found for deviceId: ${deviceId}`);
        console.warn('[ReplayContext] Available deviceIds:', Array.from(controlsRef.current.keys()));
      }
    } else {
      // Start all replays
      console.log('[ReplayContext] Starting all replays');
      controlsRef.current.forEach((controls, id) => {
        console.log(`[ReplayContext] Starting replay for deviceId: ${id}`);
        controls.startPlay();
      });
    }
  };

  const pauseReplay = (deviceId?: string) => {
    if (deviceId) {
      const controls = controlsRef.current.get(deviceId);
      controls?.pausePlay();
    } else {
      // Pause all replays
      controlsRef.current.forEach((controls) => controls.pausePlay());
    }
  };

  const resetReplay = (deviceId?: string) => {
    if (deviceId) {
      const controls = controlsRef.current.get(deviceId);
      controls?.resetPath();
    } else {
      // Reset all replays
      controlsRef.current.forEach((controls) => controls.resetPath());
    }
  };

  const startAllReplays = () => {
    controlsRef.current.forEach((controls) => controls.startPlay());
  };

  const pauseAllReplays = () => {
    controlsRef.current.forEach((controls) => controls.pausePlay());
  };

  const resetAllReplays = () => {
    controlsRef.current.forEach((controls) => controls.resetPath());
  };

  const navigateToNextPoint = (deviceId: string) => {
    const controls = controlsRef.current.get(deviceId);
    if (controls?.navigateToNextPoint) {
      controls.navigateToNextPoint();
    }
  };

  const navigateToPrevPoint = (deviceId: string) => {
    const controls = controlsRef.current.get(deviceId);
    if (controls?.navigateToPrevPoint) {
      controls.navigateToPrevPoint();
    }
  };

  const getCurrentPointInfo = (deviceId: string) => {
    const controls = controlsRef.current.get(deviceId);
    if (controls?.getCurrentPointInfo) {
      return controls.getCurrentPointInfo();
    }
    return null;
  };

  return (
    <ReplayControlContext.Provider
      value={{
        registerReplayControl,
        unregisterReplayControl,
        startReplay,
        pauseReplay,
        resetReplay,
        startAllReplays,
        pauseAllReplays,
        resetAllReplays,
        navigateToNextPoint,
        navigateToPrevPoint,
        getCurrentPointInfo,
      }}
    >
      {children}
    </ReplayControlContext.Provider>
  );
};

// Custom hook to use the context
export const useReplayControl = () => {
  const context = useContext(ReplayControlContext);
  if (context === undefined) {
    throw new Error('useReplayControl must be used within a ReplayControlProvider');
  }
  return context;
};
